import { useState, useEffect, useRef, useCallback } from "react";

// navigator.onLine is unreliable on some devices (can stay true even when
// offline in Chrome DevTools). This probe does a lightweight HEAD request to
// confirm actual connectivity when navigator.onLine reports true.
const PROBE_URL = "/favicon.ico";
const PROBE_TIMEOUT_MS = 3000;

async function probeConnectivity(): Promise<boolean> {
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
    await fetch(PROBE_URL, {
      method: "HEAD",
      cache: "no-store",
      signal: controller.signal,
    });
    clearTimeout(timer);
    return true;
  } catch {
    return false;
  }
}

export function useOnlineStatus(): { isOnline: boolean } {
  const [isOnline, setIsOnline] = useState<boolean>(true);
  const probeInFlight = useRef(false);

  const runProbe = useCallback(() => {
    if (probeInFlight.current) return;
    probeInFlight.current = true;
    void probeConnectivity().then((reachable) => {
      probeInFlight.current = false;
      setIsOnline(reachable);
    });
  }, []);

  useEffect(() => {
    if (typeof navigator === "undefined") return;

    if (!navigator.onLine) {
      setIsOnline(false);
    } else {
      runProbe();
    }

    const handleOnline = () => {
      runProbe();
    };
    const handleOffline = () => setIsOnline(false);

    const handleVisibility = () => {
      if (document.visibilityState === "visible" && navigator.onLine) {
        runProbe();
      }
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [runProbe]);

  return { isOnline };
}
